import type {
  ArchivedFilter,
  ArchiveHistoryEntry,
} from '~/features/list-archive/types/list-archive.types';

import type { ArmContentAttributes, DraftGroupingFilter } from './experiment-draft.types';
import type { AppliedProductFilters } from './product-metadata.types';

/**
 * Experiment lifecycle states. Values mirror the workflow-service contract
 * (`ExperimentResponseDto.status`) exactly, per the State-Transitions
 * reference: DRAFT → CREATED → RUNNING → COMPLETED / CANCELLED / REJECTED.
 * Terminal states: COMPLETED (ran to completion), CANCELLED (operator stopped
 * via POST /stop), REJECTED (operator declined at the approval gate — BUG-029).
 * Fine-grained "where in the journey" signal lives on `ExperimentPhase`
 * (post-creation) and the per-flow auxiliary status fields
 * (scoring/sample/pipeline); do not derive further states on the FE.
 */
export const ExperimentStatus = {
  DRAFT: 'DRAFT',
  CREATED: 'CREATED',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  /** Operator declined at the approval gate. Terminal — backend sets this via
   *  `signalPipelineApproval(REJECTED)`. Distinct from CANCELLED (POST /stop). */
  REJECTED: 'REJECTED',
  /** Pipeline step failed (e.g. full-catalog generation, delivery). `experimentPhase`
   *  records where the failure occurred and drives the row click destination. */
  FAILED: 'FAILED',
} as const;

export type ExperimentStatus = (typeof ExperimentStatus)[keyof typeof ExperimentStatus];

/**
 * Post-creation journey step — drives the dashboard's "where in the
 * post-launch flow are we." Mirrors the State-Transitions reference. Phase
 * only advances on green-path workflow events; a failure freezes it.
 */
export const ExperimentPhase = {
  CURRENT_CONTENT_SCORE: 'CURRENT_CONTENT_SCORE',
  GENERATE_SAMPLE_CONTENT: 'GENERATE_SAMPLE_CONTENT',
  REVIEW_AND_REFINE: 'REVIEW_AND_REFINE',
  FULL_CATALOG_GENERATION: 'FULL_CATALOG_GENERATION',
  ENRICHMENT_OVERVIEW: 'ENRICHMENT_OVERVIEW',
  LAUNCH: 'LAUNCH',
  LIVE: 'LIVE',
} as const;

export type ExperimentPhase =
  | (typeof ExperimentPhase)[keyof typeof ExperimentPhase]
  | (string & {});

export const ExperimentPlatform = {
  GOOGLE: 'google',
  META: 'meta',
} as const;

export type ExperimentPlatform = (typeof ExperimentPlatform)[keyof typeof ExperimentPlatform];

/**
 * How the experiment's products were partitioned into Control / Test arms. Mirrors
 * `ExperimentSplitMode` on the workflow-service side. `SCORED` is the healthy
 * default — the splitter consumed analytics ad-performance and produced
 * baseline-matched arms. `RANDOM_FALLBACK` means analytics returned no rows
 * (brand-new tenant, ad-account ingestion lag, ID mismatch), so a round-robin
 * assignment was used and the detail page must surface a banner.
 */
export const ExperimentSplitMode = {
  SCORED: 'SCORED',
  RANDOM_FALLBACK: 'RANDOM_FALLBACK',
} as const;

export type ExperimentSplitMode = (typeof ExperimentSplitMode)[keyof typeof ExperimentSplitMode];

export interface ExperimentLiftMetrics {
  ctrLift: number | null;
  cvrLift: number | null;
  revenueLift: number | null;
  roasLift: number | null;
}

export interface Experiment {
  id: string;
  name: string;
  /** `null` when the workflow API does not yet expose a SKU count for this experiment. */
  skuCount: number | null;
  armCount: number;
  /** `null` when the backend does not expose a platform for this experiment. */
  platform: ExperimentPlatform | null;
  status: ExperimentStatus;
  /**
   * Post-creation journey step (CURRENT_CONTENT_SCORE → … → LIVE). `null`
   * pre-trigger and on legacy rows; consumers should treat null as "not yet
   * entered the post-creation flow".
   */
  experimentPhase: ExperimentPhase | null;
  /** Latest scoring-failure message, or `null` when no failure recorded. Drives the list-row failure-recency badge. */
  scoringFailureReason: string | null;
  /**
   * How the splitter built the Control / Test arms. `null` on legacy rows
   * created before the column existed — treated identically to `SCORED` by
   * consumers (no banner). `RANDOM_FALLBACK` surfaces the
   * "arms aren't baseline-matched" banner on the detail page.
   */
  splitMode: ExperimentSplitMode | null;
  /** Extra days appended to the test once results are inconclusive at the original end. `null` when never extended. */
  extendedDays: number | null;
  progressDays: number;
  metrics: ExperimentLiftMetrics;
  alertCount: number;
  createdAt: string;
  updatedAt: string;
  /** Audit stamps (workflow-service contract). The actor is a string — email /
   *  WORKFLOW-SERVICE / service token / UNKNOWN / null — rendered via
   *  `ActorStringLabel`. Optional: older builds and legacy rows may omit them. */
  createdBy?: string | null;
  lastActionBy?: string | null;
  lastActionAt?: string | null;
  /** List Archive visibility stamp — set when the experiment is hidden from the
   *  default list. Derive `archived` from it (`isArchived(row)`); never a separate
   *  boolean. Optional: pre-rollout backends omit it. */
  archivedAt?: string | null;
  /** Who archived it — populated only while archived; `null` on rows archived
   *  before changeset 013 (render nothing, not a placeholder). */
  archivedBy?: string | null;
}

export interface ExperimentStatsAttention {
  critical: number;
  warning: number;
}

export interface ExperimentStats {
  activeExperiments: number;
  platformBreakdown: Record<string, number>;
  totalRevenueImpact: number;
  needsAttention: ExperimentStatsAttention;
}

export interface ExperimentListParams {
  page: number;
  pageSize: number;
  search?: string | undefined;
  statuses?: ExperimentStatus[] | undefined;
  dateRange?: string | undefined;
  /** List Archive visibility filter — omit for the default list, `ARCHIVED` for
   *  the Archived view, `ALL` for existence probes. */
  archived?: ArchivedFilter | undefined;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const CheckpointStatus = {
  PASSED: 'passed',
  IN_PROGRESS: 'in_progress',
  PENDING: 'pending',
  WARNING: 'warning',
  FAILED: 'failed',
} as const;

export type CheckpointStatus = (typeof CheckpointStatus)[keyof typeof CheckpointStatus];

export type CheckpointIcon = 'gear' | 'people';

export interface TimelineCheckpoint {
  id: string;
  dayLabel: string;
  name: string;
  status: CheckpointStatus;
  details: string;
  icon: CheckpointIcon;
  /** Source of the revenue `details` quotes (Results Readout only); null elsewhere. */
  revenueSource?: RevenueSource | null;
}

export interface ExperimentSegmentOption {
  value: string;
  label: string;
}

export interface ExperimentDetail extends Experiment {
  runningSinceDays: number;
  segments: ExperimentSegmentOption[];
  /** Primary KPI persisted on the strategy step (e.g. CTR, CVR, ROAS). `null` for pre-strategy or legacy rows. */
  primaryKpi: string | null;
  /** Secondary KPI persisted on the strategy step. `null` when none chosen. */
  secondaryKpi: string | null;
  /** Content attributes selected for enrichment (e.g. ["TITLE","DESCRIPTION"]). `null` for legacy rows. */
  contentAttributes: string[] | null;
  /** Per-enriched-arm selection (`{ B: [...], C: [...] }`). `null` when the run used a single selection. */
  armContentAttributes: ArmContentAttributes | null;
  /** matchBy code (`ITEM_GROUP_ID`, `PARENT_ID`, `SKU`) chosen on draft create. `null` for legacy rows. */
  parentIdField: string | null;
  /** Control-first traffic-split percentages, e.g. `[50, 25, 25]`. `null` when not yet committed. */
  trafficSplit: number[] | null;
  /** Step-2 scope filters — sent and returned as-is. `null` for legacy/CREATED rows that never carried scope. */
  scopingFilters: AppliedProductFilters | null;
  /** Step-3 grouping filter, wire shape. `null` when "no grouping" was chosen. */
  groupingFilter: DraftGroupingFilter | null;
  /** Catalog snapshot id the draft was pinned to during step 2. `null` for
   *  legacy / CREATED rows that pre-date snapshot pinning; downstream callers
   *  (content-summary, scoring, enrichment) treat null as "use the latest run". */
  syncId: string | null;
  /** List Archive visibility history, newest-first (max 50). Detail payloads
   *  only — list rows never carry it. `null`/absent on pre-014 builds. */
  archiveHistory?: ArchiveHistoryEntry[] | null;
}

/** Analytics revenue source GA4 → Adobe Analytics → Google Ads, in fallback order. */
export type RevenueSourceId = 'google_analytics' | 'adobe_analytics' | 'google_ads';

/**
 * Where an analytics endpoint's revenue figures come from. Decided once per
 * experiment server-side; `is_fallback` is true when GA4 (then Adobe) had no
 * revenue. Optional everywhere — older responses and data-less experiments omit it.
 */
export interface RevenueSource {
  source: RevenueSourceId;
  source_label: string;
  is_fallback: boolean;
  fallback_order: RevenueSourceId[];
  unavailable_sources: RevenueSourceId[];
  /** Formula, e.g. "Google Ads conversion value". */
  logic: string;
  /** Ready-made caption, e.g. "Revenue · Google Ads (GA4 / Adobe Analytics not available)". */
  description: string;
}

export interface ExperimentKpis {
  daysElapsed: number;
  /**
   * Planned duration in days. `null` when the experiment has no defined end
   * (e.g. analytics summary reports `end_date: null` for an open-ended run);
   * the UI then renders the elapsed count without an "of N" suffix.
   */
  totalDays: number | null;
  totalImpressions: number;
  totalClicks: number;
  totalConversions: number;
  totalRevenue: number;
  /** Source of `totalRevenue` (summary `total_revenue_source`). */
  revenueSource?: RevenueSource | null;
}

export const PerformanceMetric = {
  REVENUE_LIFT: 'revenue_lift',
  CONV_VALUE_LIFT: 'conv_value_lift',
  CONVERSIONS_LIFT: 'conversions_lift',
  IMPRESSIONS_LIFT: 'impressions_lift',
  CTR_LIFT: 'ctr_lift',
  CVR_LIFT: 'cvr_lift',
  ROAS_LIFT: 'roas_lift',
  CPC_LIFT: 'cpc_lift',
} as const;

export type PerformanceMetric = (typeof PerformanceMetric)[keyof typeof PerformanceMetric];

export interface PerformanceSeriesPoint {
  day: number;
  testA: number;
  testB: number;
  control: number;
}

export interface PerformanceAnnotation {
  day: number;
  label: string;
}

/** Which arm slots actually had impressions in the cumulative window. Used by */
/** consumers to skip absent arms instead of drawing misleading 0% baselines.   */
export type ArmSlot = 'testA' | 'testB' | 'control';

export interface PerformanceResponse {
  metric: PerformanceMetric;
  yAxisLabel: string;
  series: PerformanceSeriesPoint[];
  annotations: PerformanceAnnotation[];
  startDay: number;
  endDay: number;
  /** Arms with non-zero cumulative impressions over the window; `control` is */
  /** always included as the baseline reference even when its value is 0.    */
  armsPresent: ArmSlot[];
}

// ---------------------------------------------------------------------------
// Arm Comparison
// ---------------------------------------------------------------------------

export const ArmId = {
  CONTROL: 'control',
  TEST_A: 'test_a',
  TEST_B: 'test_b',
} as const;

export type ArmId = (typeof ArmId)[keyof typeof ArmId];

export interface ArmComparisonRow {
  metric: string;
  unit?: string;
  control: string;
  testA: string;
  testB: string;
  testALift: string;
  testBLift: string;
  /** Arm holding the highest value in this row — drives the "best value" accent
   *  dot. `null` when the row has no values. See `utils/arm-lift.ts`. */
  bestArm: ArmSlot | null;
  /** Where this row's figures come from; null when the backend sent no source. */
  source?: { source_label: string; description: string; is_fallback: boolean } | null;
}

export interface ArmComparisonResponse {
  /** Winner as reported by the analytics service (`winner: "A" | "B" | "C"`),
   *  mapped from the domain arm letter. `null` when the backend reports no
   *  winner, so the UI shows none instead of defaulting to Control (AGENT-196). */
  winner: ArmId | null;
  /** Arms that have non-null data rows; used by consumers to hide absent columns. */
  armsPresent: ArmId[];
  rows: ArmComparisonRow[];
}

// ---------------------------------------------------------------------------
// ROAS Comparison (two-source)
// ---------------------------------------------------------------------------

export type RoasLiftDirection = 'positive' | 'negative' | 'neutral';

/** `testB*` fields are `null` when the experiment runs a single test arm. */
export interface RoasComparisonRow {
  metric: string;
  /** Drives the "why two ROAS figures" tooltip; set on the `roas` row of every section. */
  isRoas: boolean;
  control: string;
  testA: string;
  didLift: string;
  didLiftDirection: RoasLiftDirection;
  testB: string;
  testBDidLift: string;
  testBDidLiftDirection: RoasLiftDirection;
}

export interface RoasComparisonSection {
  sourceLabel: string;
  rows: RoasComparisonRow[];
}

export interface RoasComparisonResponse {
  /** Baseline arm letter from the payload; `null` when the API doesn't send one. */
  controlArm: string | null;
  /**
   * Test arm letters in column order. Drives how many test columns render —
   * a null check on `testB` can't, since null also means "arm exists but has no
   * ad spend". Empty when the API predates the per-arm split.
   */
  testArms: string[];
  sections: RoasComparisonSection[];
}

// ---------------------------------------------------------------------------
// Daily DID lift analysis
// ---------------------------------------------------------------------------

export interface DailyLiftRow {
  day: number;
  gmcImpr: number;
  adsImpr: number;
  gmcClicks: number;
  adsClicks: number;
  convValue: number;
  conversions: number;
  sessions: number;
  revenue: number;
}

export interface DailyLiftTrendPoint {
  day: number;
  testA: number;
  testB: number;
  control: number;
}

export interface DailyLiftResponse {
  rows: DailyLiftRow[];
  trend: DailyLiftTrendPoint[];
  /** Source of `rows[].revenue` and the trend. */
  revenueSource?: RevenueSource | null;
  /** Same semantics as `PerformanceResponse.armsPresent` — consumers should */
  /** filter their SERIES_META by this so absent test arms don't draw a 0% line. */
  armsPresent: ArmSlot[];
}

// ---------------------------------------------------------------------------
// Health tab
// ---------------------------------------------------------------------------

export type HealthStatTone = 'default' | 'success' | 'warning' | 'error';

export interface HealthKpi {
  label: string;
  value: string;
  hint?: string;
  tone?: HealthStatTone;
}

export interface HealthTrendPoint {
  day: number;
  test: number;
  control: number;
}

export interface HealthInStockTrends {
  kpis: HealthKpi[];
  series: HealthTrendPoint[];
}

export type CategoryExclusionMetric =
  | 'gmcImpressions'
  | 'gmcClicks'
  | 'sessions'
  | 'conversionValue'
  | 'revenue';

export interface CategoryExclusionCell {
  control: string;
  test: string;
}

export interface CategoryExclusionRow {
  label: string;
  values: Record<CategoryExclusionMetric, CategoryExclusionCell>;
}

export interface CategoryExclusionsResponse {
  rows: CategoryExclusionRow[];
  highlightedColumn: CategoryExclusionMetric;
  /** Source of the revenue column (camelCase, like its siblings). */
  revenueSource?: RevenueSource | null;
}

export interface AdjustedMetricsRow {
  arm: string;
  duration: number;
  parentIds: number;
  childIds: number;
  prePeriodRevenue: string;
  postPeriodRevenue: string;
}

export interface AdjustedMetricsCallout {
  title: string;
  body: string;
}

export interface AdjustedMetricsResponse {
  rows: AdjustedMetricsRow[];
  callouts: AdjustedMetricsCallout[];
  /** Source of the pre/post period revenue (camelCase, like its siblings). */
  revenueSource?: RevenueSource | null;
}

// ---------------------------------------------------------------------------
// Category-mode Performance (screenshot 15 / 17)
// Discriminator: tenant-level Configuration Manager parameter
// `experiment_details_performance_view` (TBD — not yet on the parameter list).
// ---------------------------------------------------------------------------

/** Single per-week DiD percentage point for the line chart (e.g. Week 1 = -3.0%). */
export interface WeeklyDidRevenuePoint {
  weekLabel: string;
  /** DiD percent as a decimal fraction (e.g. -0.03 = -3%). Renderer multiplies by 100. */
  didPct: number;
}

/** Single per-week test vs. control revenue pair for the grouped-bar chart. */
export interface TestVsControlRevenuePoint {
  weekLabel: string;
  test: number;
  control: number;
}

/** Option in the category dropdown that drives the Weekly DiD Revenue charts. */
export interface CategoryFilterOption {
  value: string;
  label: string;
}

/** Bullet-row entry for the chatbot's "Category Standouts" labeled card. */
export interface CategoryStandout {
  label: string;
  primary: string;
  /** Optional sub-line rendered under the primary value (e.g. "+51.5%", "Revenue Share 51.2%"). */
  subValue?: string;
  /** Sign treatment for the sub-value when it carries a numeric delta. */
  subValueTrend?: 'up' | 'down' | 'neutral';
}

/** Overall DID lift summary shown at the top of the chatbot category narration. */
export interface CategoryOverallDidLift {
  revenuePct: number;
  conversionValuePct: number;
  conversionsPct: number;
  impressionsPct: number;
}

/** Six-cell lift KPI row at the top of the right-side category Performance panel. */
export interface CategoryKpis {
  revenueLift: number;
  conversionValueLift: number;
  conversionLift: number;
  impressionLift: number;
  clickLift: number;
  sessionLift: number;
}

/** Row in the right-side "Category Performance — Revenue Weighted" table. */
export interface CategoryPerformanceRow {
  category: string;
  /** Revenue contribution as a percent (0–100); rendered as an intensity bar. */
  revContributionPct: number;
  /** Signed DID percent for impressions. */
  didImpressionPct: number;
  /** Signed DID percent for clicks. */
  didClicksPct: number;
  /** Signed DID percent for conversions. */
  didConversionsPct: number;
  /** Signed DID percent for conversion value. */
  didConvValuePct: number;
}

/** Row in the right-side "Daily DID Lift Analysis" table (categorical version). */
export interface CategoryDailyDidLiftRow {
  category: string;
  weekLabel: string;
  /** ISO date string (YYYY-MM-DD). */
  weekStart: string;
  testPreAvg: number;
  testPostRev: number;
  controlPreAvg: number;
  controlPostRev: number;
  /** Signed DID revenue percent. */
  didRevenuePct: number;
}

/** Icon class for a "Detailed Data Downloads" card. */
export type DataDownloadIcon = 'folder' | 'chart';

/** Single CSV-download card in the right-side "Detailed Data Downloads" grid. */
export interface DataDownloadCard {
  id: string;
  icon: DataDownloadIcon;
  title: string;
  description: string;
  /** Sub-line summary (e.g. "11 categories · 8 metrics"). */
  summary: string;
}

/** Single fetch returning every piece the category-mode UI needs (chatbot + main panel). */
export interface CategoryPerformanceResponse {
  /** Source of every revenue figure in the category-mode panel. */
  revenueSource?: RevenueSource | null;
  /** Categories the user can pick from in the Weekly DiD Revenue Trend dropdown. */
  availableCategories: CategoryFilterOption[];
  /** KPI row at top of the right-side panel. Optional: the analytics
   *  `/category-performance` endpoint returns no overall aggregate yet, so the
   *  KPI cards render an empty "—" state until it does. */
  kpis?: CategoryKpis;
  /** Chatbot left-side "Overall DID lift" summary. Optional for the same reason
   *  as `kpis` — omitted until the endpoint exposes an overall. */
  overallDidLift?: CategoryOverallDidLift;
  /** Chatbot left-side "Category Standouts" rows. */
  categoryStandouts: CategoryStandout[];
  /** Line-chart points for the selected category. */
  weeklyDidRevenue: WeeklyDidRevenuePoint[];
  /** Grouped-bar points for the selected category. */
  testVsControlRevenue: TestVsControlRevenuePoint[];
  /** Per-category metrics — the right-side "Category Performance — Revenue Weighted" table. */
  categoryPerformanceRows: CategoryPerformanceRow[];
  /** Per-category daily DID lift rows (for the selected category's date breakdown). */
  dailyDidLift: CategoryDailyDidLiftRow[];
}
